import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { format } from 'date-fns';

import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  debounce,
  FormControl,
  IconButton,
  InputBase,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useTheme
} from "@mui/material";
import type { GridColDef, GridPaginationModel, GridRowParams } from "@mui/x-data-grid";
import { AddCircleOutline, EditSquare, SearchOutlined } from "@mui/icons-material";

import { RootState } from "../../store/Index";
import { DataGrid } from "../../components/common/Index";
import {
  CreateRateDialog,
  DeleteRateCardDialog,
  EditRateDialog,
} from "./components";

import apiService from "../../service/Api";

import { TrashIcon } from "../../components/icons";
import {
  getRateAmount,
  IFormProps,
  IRateCardProps,
  isRateCardRateType,
  RateCardRateType,
} from "./types/rate-card";
import { tokens } from "../../theme";
import { User } from "../../store/slice/AuthSlice";
import { AxiosError } from "axios";
import ViewRateCardDialog from "./components/ViewRateCardDialog";

const createEmptyFormData = (): IFormProps => ({
  role: {
    name: "",
    description: ""
  },
  roleId: null,
  hourly_rate: 0,
  stream_rate: 0,
  daily_rate: 0,
  salaried_rate: 0,
  fixed_rate: 0,
  currency: "USD",
  effective_date: "",
  last_updated_by: ""
});

const getSelectedRoleLabel = (rateCard: IRateCardProps | null) => {
  if (!rateCard?.role?.id || !rateCard.role?.name) {
    return undefined;
  }

  return `${rateCard.role.id} - ${rateCard.role.name}`;
};

const getFormDataFromRateCard = (rateCard: IRateCardProps): IFormProps => {
  const currentRateType = isRateCardRateType(rateCard.rating_type)
    ? rateCard.rating_type
    : "fixed";
  const currentRateAmount =
    getRateAmount(rateCard.rate, currentRateType) ||
    getRateAmount(rateCard.rate, "fixed");
  const currentCurrency =
    rateCard.rate?.[currentRateType]?.currency ?? rateCard.currency ?? "USD";

  return {
    role: {
      name: rateCard.role?.name ?? "",
      description: rateCard.role?.description ?? "",
    },
    roleId: rateCard.role_id ?? rateCard.role?.id ?? null,
    hourly_rate: getRateAmount(rateCard.rate, "hourly"),
    stream_rate: getRateAmount(rateCard.rate, "stream"),
    daily_rate: getRateAmount(rateCard.rate, "daily"),
    salaried_rate: getRateAmount(rateCard.rate, "salaried"),
    fixed_rate: currentRateAmount,
    currency: currentCurrency === "ZWG" ? "ZWG" : "USD",
    effective_date: rateCard.effective_date,
    last_updated_by: rateCard.last_updated_by,
  };
};

const getDisplayRateType = (rateCard: IRateCardProps): RateCardRateType =>
  isRateCardRateType(rateCard.rating_type) ? rateCard.rating_type : "fixed";

const RateCards: React.FC = () => {
  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10
  });
  const [rowCount, setRowCount] = useState(0);
  const [crewRates, setCrewRates] = useState<IRateCardProps[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string | undefined>();
  const [formData, setFormData] = useState<IFormProps>(createEmptyFormData);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<"all" | RateCardRateType>("all");
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [openCreateRateDialog, setOpenCreateRateDialog] = useState<boolean>(false);
  const [rateType, setRateType] = useState<RateCardRateType>("fixed");
  const [openViewRateCardDialog, setOpenViewRateCardDialog] = useState<boolean>(false);
  const [openEditRateDialog, setOpenEditRateDialog] = useState<boolean>(false);
  const [openDeleteRateDialog, setOpenDeleteRateDialog] = useState<boolean>(false);
  const [selectedRateCard, setSelectedRateCard] = useState<IRateCardProps | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  const user = useSelector((state: RootState) => state.auth.user) as User | null;

  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  const primaryColor = colors.primary[400];
  const primaryTextColor = colors.primary[400];
  const secondaryTextColor = colors.neutral[100];
  const backgroundColor = colors.primary[800] || "#A9B2C3";

  const handleViewRateCard = () => {
    setOpenViewRateCardDialog(true);
  };

  const handleCloseViewRateCard = () => {
    setOpenViewRateCardDialog(false);
    setSelectedRateCard(null);
  };

  const fetchRates = useCallback(
    async () => {
      setLoading(true);
      setError(null);
      setWarning(null);

      try {
        const apiPage = pagination.page + 1;
        const params = {
          page: apiPage,
          page_size: pagination.pageSize,
          search: searchTerm || undefined
        };

        const response = await apiService.get<{
          count: number;
          results: IRateCardProps[];
        }>("/api/crews/role-ratings/", params);

        setCrewRates(response.data.results);
        setRowCount(response.data.count);
      } catch (error) {
        if (error instanceof AxiosError) {
          const errorMessage = error.response?.data?.message || "Failed to load rates. Please try again.";
          setError(errorMessage);
        } else {
          console.log({ error }, 'fetchRates error');
        }
      } finally {
        setLoading(false);
      }
    },
  [pagination, searchTerm]);

  useEffect(() => {
    fetchRates();
  }, [fetchRates])

  const columns: GridColDef<IRateCardProps>[] = [
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ lineHeight: 1, py: 2 }}>
          <Typography variant="subtitle2">
            {params.row.role.name}
          </Typography>
        </Box>
      ),
    },
    {
      field: "rate",
      headerName: "Rates",
      flex: 1,
      renderCell: (params) => {
        const displayRateType = getDisplayRateType(params.row);
        const rateAmount = getRateAmount(params.row.rate, displayRateType);
        const currency =
          params.row.rate?.[displayRateType]?.currency ??
          params.row.currency ??
          "USD";

        return (
          <Box sx={{ lineHeight: 1, py: 2 }}>
            <Typography variant="subtitle2">
              {`${displayRateType.toUpperCase()} Rate: ${currency} ${rateAmount}`}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "effective_date",
      headerName: "Effective Date",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ py: 2, lineHeight: 1 }}>
          <Typography variant={'caption'}>
            {format(new Date(params.row.effective_date), 'MMM dd, yyyy')}
          </Typography>
        </Box>
      ),
    },
    {
      field: "last_updated_by",
      headerName: "Updated By",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", lineHeight: 1, my: 1, alignItems: "center", gap: 1 }}>
          <Avatar
            src={''}
            sx={{
              width: 32,
              height: 32,
              bgcolor: colors.primary[800],
              color: colors.primary[100],
            }}
          />
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {params.row.last_updated_by?.length ? params.row.last_updated_by : "N/A"}
            </Typography>
            <Typography variant="caption" fontWeight="medium">
              {`ID: ${params.row.last_updated_by?.length ? params.row.last_updated_by : "N/A"}`}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <Box display={'flex'} alignItems={'center'} sx={{ py: 1, lineHeight: 1 }} gap={1}>
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              setSelectedRateCard(params.row);
              setFormData(getFormDataFromRateCard(params.row));
              setSelectedRoles(getSelectedRoleLabel(params.row));
              setRateType("fixed");
              setOpenEditRateDialog(true);
            }}>
            <EditSquare color="primary" sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              setSelectedRateCard(params.row);
              setOpenDeleteRateDialog(true);
            }}
          >
            <TrashIcon color="error" sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      ),
    },
  ];

  const resetForm = useCallback(() => {
    setFormData(createEmptyFormData());
  }, []);

  const clearRateFormState = useCallback(() => {
    setSelectedRoles(undefined);
    setRateType("fixed");
    resetForm();
  }, [resetForm]);

  const handleOpenCreateRateDialog = () => {
    setSelectedRateCard(null);
    clearRateFormState();
    setOpenCreateRateDialog(true);
  };

  const handleCloseEditRateDialog = () => {
    setOpenEditRateDialog(false);
    setSelectedRateCard(null);
    clearRateFormState();
  };

  const handleCloseDeleteRateDialog = () => {
    setOpenDeleteRateDialog(false);
    setSelectedRateCard(null);
  };

  const validateRateForm = () => {
    const {
      effective_date,
      fixed_rate,
      roleId,
      currency,
    } = formData;

    if (!effective_date) {
      toast.error("Please provide the effective date for this rate card.");
      return false;
    }

    if (!roleId) {
      toast.error("Please select the role for this rate card.");
      return false;
    }

    if (!fixed_rate) {
      toast.error("Please provide the rate amount for this rate card.");
      return false;
    }

    if (!currency) {
      toast.error("Please select the currency for this rate card.");
      return false;
    }

    return true;
  };

  const buildRatePayload = () => {
    const rateAmount = Number(formData.fixed_rate ?? 0);
    const selectedRateType: RateCardRateType = rateType || "fixed";
    const baseRateValue = {
      amount: 0,
      currency: formData.currency,
      overtime_multiplier: 0,
      night_premium: 0,
      notes: "",
    };
    const rates = {
      hourly: { ...baseRateValue },
      stream: { ...baseRateValue },
      daily: { ...baseRateValue },
      salaried: { ...baseRateValue },
      fixed: { ...baseRateValue },
    };

    rates[selectedRateType] = {
      ...baseRateValue,
      amount: rateAmount,
    };

    return {
      role: {
        name: formData.role.name,
        description: formData.role.description
      },
      role_id: formData.roleId,
      rate: rates,
      currency: formData.currency,
      effective_date: formData.effective_date,
      rating_type: selectedRateType,
    };
  };

  const onRateCreation = async () => {
    if (!validateRateForm()) return;

    setFormLoading(true);
    let shouldCloseDialog = false;
    try {
      const ratePayload = buildRatePayload();

      await apiService.post("/api/crews/role-ratings/", ratePayload);

      toast.success("Rate card created successfully!", {
        position: "top-right",
        autoClose: 2000,
        theme: "light",
      });
      shouldCloseDialog = true;
      fetchRates();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.message || "Failed to create a rate card. Please try again.", {
          position: "top-right",
          autoClose: 2000,
          theme: "light",
        });
      } else {
        console.log('Failed to create a rate card', error);
      }
    } finally {
      setFormLoading(false);
      if (shouldCloseDialog) {
        setOpenCreateRateDialog(false);
        clearRateFormState();
      }
    }
  };

  const onRateEdit = async () => {
    if (!selectedRateCard?.id) {
      toast.error("Please select a rate card to edit.");
      return;
    }

    if (!validateRateForm()) return;

    setFormLoading(true);
    let shouldCloseDialog = false;
    try {
      const ratePayload = {
        ...buildRatePayload(),
        created_by: user?.id,
        last_updated_by: user?.id,
      };

      await apiService.patch(`/api/crews/role-ratings/${selectedRateCard.id}/`, ratePayload);

      toast.success("Rate card updated successfully!", {
        position: "top-right",
        autoClose: 2000,
        theme: "light",
      });
      shouldCloseDialog = true;
      fetchRates();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.message || "Failed to update rate card. Please try again.", {
          position: "top-right",
          autoClose: 2000,
          theme: "light",
        });
      } else {
        console.log('Failed to update rate card', error);
      }
    } finally {
      setFormLoading(false);
      if (shouldCloseDialog) {
        handleCloseEditRateDialog();
      }
    }
  };

  const onDeleteRateCard = async () => {
    if (!selectedRateCard?.id) {
      toast.error("Please select a rate card to delete.");
      return;
    }

    setDeleteLoading(true);
    try {
      await apiService.delete(`/api/crews/role-ratings/${selectedRateCard.id}/`);

      toast.success("Rate card deleted successfully!", {
        position: "top-right",
        autoClose: 2000,
        theme: "light",
      });

      handleCloseDeleteRateDialog();
      fetchRates();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.message || "Failed to delete rate card. Please try again.", {
          position: "top-right",
          autoClose: 2000,
          theme: "light",
        });
      } else {
        console.log("Failed to delete rate card", error);
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setSearchTerm(value), 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.clear();
    };
  }, [debouncedSetSearchTerm]);

  const handleRolesChange = (role: string | null) => {
    if (role) {
      const regexed = role.match(/^(.*?)\s-\s(.*)$/);

      setSelectedRoles(role);
      if (regexed) {
        setFormData((prev: IFormProps) => (
          {
            ...prev,
            role: {
              name: regexed[2],
              description: regexed[2]
            },
            roleId: parseInt(regexed[1])
          }
        ));
      }
    }
  };

  const handleRolesEdit = (role: string | null) => {
    if (role) {
      const regexed = role.match(/^(.*?)\s-\s(.*)$/);

      setSelectedRoles(role);
      if (regexed) {
        setFormData((prev: IFormProps) => (
          {
            ...prev,
            role: {
              name: regexed[2],
              description: regexed[2]
            },
            roleId: parseInt(regexed[1])
          }
        ));
      }
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handling pagination when user changes the pagination model
  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPagination(newModel);
  };

  const handleRowClick = (params: GridRowParams<IRateCardProps>) => {
    setSelectedRateCard(params.row);
    handleViewRateCard();
  };

  if (!crewRates) return null;

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Box sx={{ height: "100%", background: colors.primary[900], padding: "24px", borderRadius: 5, boxShadow: 0 }}>
        <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} mb={1} gap={1}>
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <Box
              sx={{
                backgroundColor: backgroundColor,
                border: '1px solid',
                borderRadius: 2,
                borderColor: 'primary',
                display: "flex",
                alignItems: "center",
                pl: 1,
                flex: 1,
                minWidth: 320,
              }}
            >
              <SearchOutlined sx={{ fontSize: 24, mr: 1, color: secondaryTextColor }} />
              <InputBase
                placeholder="Search by role..."
                sx={{ padding: .4, flex: 1, color: primaryTextColor }}
                onChange={(e) => debouncedSetSearchTerm(e.target.value)}
              />
            </Box>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel
                id="rate-type-label"
                sx={{ color: primaryTextColor }}
              >
                Rate Type
              </InputLabel>
              <Select
                labelId="rate-type-label"
                id="rate-type-label"
                value={filterType}
                label="Rate Type"
                onChange={(e) => setFilterType(e.target.value)}
                sx={{
                  background: backgroundColor,
                  color: primaryTextColor,
                  fontSize: 12,
                  borderRadius: 2,
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: primaryColor,
                  },
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="hourly">Hourly</MenuItem>
                <MenuItem value="stream">Per Stream</MenuItem>
                <MenuItem value="daily">Per Day</MenuItem>
                <MenuItem value="fixed">Fixed</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Button
            size={"small"}
            startIcon={<AddCircleOutline />}
            onClick={handleOpenCreateRateDialog}
            sx={{ textTransform: 'capitalize' }}
            variant="contained">
              Add Rate Card
          </Button>
        </Box>

        {loading ? (
          <CircularProgress color={'primary'} size={16} />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : warning ? (
          <Alert severity="warning">{warning}</Alert>
        ) : (
          <Box>
            {crewRates.length > 0 && (
              <DataGrid
                rows={crewRates}
                columns={columns}
                rowCount={rowCount}
                loading={loading}
                paginationMode="server"
                paginationModel={pagination}
                onRowClick={handleRowClick}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={[5, 10, 15, 20]}
                sx={{
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor:
                      colors.primary[500],
                    color: colors.primary[100],
                  },
                  "& .MuiDataGrid-columnHeader": {
                    backgroundColor:
                      colors.primary[500],
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    color: colors.primary[100],
                  },
                  "& .MuiDataGrid-footerContainer": {
                    backgroundColor: colors.primary[900],
                    justifyContent: "center",
                    padding: 1,
                  },
                }}
              />
            )}
          </Box>
        )}
      </Box>
      {/* All Dialogs */}
      <CreateRateDialog
        formData={formData}
        formLoading={formLoading}
        handleCreateRate={onRateCreation}
        handleInputChange={handleInputChange}
        handleRolesChange={handleRolesChange}
        setDialogOpen={setOpenCreateRateDialog}
        open={openCreateRateDialog}
        rateType={rateType}
        resetForm={resetForm}
        selectedRoles={selectedRoles}
        setSelectedRoles={setSelectedRoles}
        setRateType={setRateType}
      />

      {selectedRateCard && (
        <EditRateDialog
          formData={formData}
          formLoading={formLoading}
          handleEditRate={onRateEdit}
          handleInputChange={handleInputChange}
          handleRolesEdit={handleRolesEdit}
          setDialogOpen={setOpenEditRateDialog}
          open={openEditRateDialog}
          rateType={rateType}
          resetForm={resetForm}
          selectedRateCard={selectedRateCard}
          selectedRoles={selectedRoles}
          setSelectedRoles={setSelectedRoles}
          setRateType={setRateType}
        />
      )}

      <ViewRateCardDialog
        open={openViewRateCardDialog}
        onClose={handleCloseViewRateCard}
        selectedRateCard={selectedRateCard}
      />

      <DeleteRateCardDialog
        deleteLoading={deleteLoading}
        onClose={handleCloseDeleteRateDialog}
        onConfirm={onDeleteRateCard}
        open={openDeleteRateDialog}
        selectedRateCard={selectedRateCard}
      />
    </Box>
  );
};

export default RateCards;
